import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { formatCurrency } from 'ddc-intl';
import logger from 'ws-scripts/modules/logger';
import { useFlags, useLabels } from 'wsm-common-data';
import useEventBus from '../hooks/useEventBus';
import usePaymentData from '../hooks/usePaymentData';
import useTrackEvent from '../hooks/useTrackEvent';
import PaymentSelectOption from './PaymentSelectOption';
import InlineDotsLoader from './InlineDotsLoader';
import DisclaimerText from './DisclaimerText';

import { setPayment } from '../features/steps/stepsSlice';
import { getLsWalletId } from '../helpers';

const PaymentSelector = () => {
	const dispatch = useDispatch();
	const [selectedId, setSelectedId] = useState(0);
	const [isLoading, hasError, paymentData] = usePaymentData();
	const [eventBus, isReady] = useEventBus();
	const sendTrackEvent = useTrackEvent();
	const flags = useFlags();
	const enablePreload =
		flags['ws-track-my-offer-preload-payment-data'] || false;
	const labels = useLabels();

	const selectedPayment =
		paymentData?.length && paymentData[selectedId]?.payment;
	const selectedDealType =
		paymentData?.length &&
		paymentData[selectedId]?.paymentLabel.toLowerCase();

	// Extract taxes and fees flags from payment data
	const showTaxesAndFeesDisclaimer = useMemo(
		() => paymentData?.some((data) => data.hasTaxesAndFees) || false,
		[paymentData]
	);

	// Component lifecycle logging
	useEffect(() => {
		logger.info('WS-TRACK-MY-OFFER: PaymentSelector mounted', {
			enablePreload,
			hasError,
			isLoading,
			paymentDataLength: paymentData?.length || 0
		});
		return () => {
			logger.warn('WS-TRACK-MY-OFFER: PaymentSelector unmounting!', {
				hasError,
				isLoading,
				paymentData: paymentData?.length || 0,
				selectedPayment,
				selectedDealType,
				enablePreload
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Log state changes
	useEffect(() => {
		logger.info('WS-TRACK-MY-OFFER: PaymentSelector state changed', {
			hasError,
			isLoading,
			paymentDataLength: paymentData?.length || 0,
			paymentData: paymentData?.map((d) => ({
				label: d.paymentLabel,
				payment: d.payment
			})),
			enablePreload
		});
	}, [hasError, isLoading, paymentData, enablePreload]);

	useEffect(() => {
		if (isReady) {
			const handleUdscalcPencilingDataUpdateRequest = (data) => {
				if (data && paymentData) {
					const paymentItemId = paymentData.findIndex(
						(item) =>
							item.paymentLabel.toLowerCase() ===
							data.dealType?.toLowerCase()
					);

					if (paymentItemId !== selectedId) {
						setSelectedId(paymentItemId);
					}
				}
			};

			eventBus.on(
				'uds:calcPencilingDataUpdate:updated',
				handleUdscalcPencilingDataUpdateRequest
			);

			return () => {
				eventBus.off(
					'uds:calcPencilingDataUpdate:updated',
					handleUdscalcPencilingDataUpdateRequest
				);
			};
		}

		// default clean up
		return () => {};
	}, [isReady, eventBus, paymentData, selectedId]);

	// Set selectedId based on the index of the object that is the dealType (isSelected)
	useEffect(() => {
		if (paymentData?.length) {
			const selectedPaymentIndex = paymentData.findIndex(
				(data) => data.isSelected
			);

			setSelectedId(
				selectedPaymentIndex !== -1 ? selectedPaymentIndex : 0
			);
		}
	}, [paymentData]);

	useEffect(() => {
		const pubsub = window?.DDC?.pubsub;
		const hasInteractedWithWallet = !!getLsWalletId();
		let paymentToPublish = null;

		if (selectedPayment && selectedDealType !== 'cash') {
			paymentToPublish = formatCurrency(selectedPayment);
		}

		if (pubsub) {
			pubsub.publish('ws-vehicle-toolbar/show-vehicle-payment', {
				payment: paymentToPublish
			});
		}
		// TODO: handle payment error
		dispatch(
			setPayment({
				complete:
					hasInteractedWithWallet &&
					!!selectedPayment &&
					!!selectedDealType,
				paymentType: selectedDealType,
				total: selectedPayment
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPayment, selectedDealType]);

	const getPlaceholders = () => {
		// If preload is enabled, render full template with inline loaders
		if (enablePreload) {
			// Render all three payment types - preload script will hide/show based on actual data
			return (
				<div
					className="w-100 d-flex"
					style={{
						justifyContent: 'space-evenly',
						position: 'relative'
					}}
					data-preload-container="payment-options"
					data-testid="payment-options"
				>
					{/* Finance Option */}
					<div
						className="position-relative payment-option-container py-3 d-flex border 
						justify-content-center border-left rounded-left left-option selected 
						border rounded"
						style={{ flex: '1 1 0px' }}
						data-payment-type="finance"
					>
						<label
							htmlFor="payment_option_0_input"
							className="my-0 font-weight-normal w-100"
						>
							<div className="d-flex flex-row justify-content-center mt-1">
								<input
									type="radio"
									className="mt-0 mr-2"
									id="payment_option_0_input"
									value="Finance"
									name="paymentSelection"
									defaultChecked
									disabled
								/>
								<span className="ddc-font-size-small">
									Finance
								</span>
							</div>
							<div className="text-center">
								<span className="font-weight-bold ddc-font-size-base">
									<InlineDotsLoader />
								</span>
								<span className="text-muted ddc-font-size-xsmall">
									{' '}
									/ mo
								</span>
							</div>
						</label>
					</div>

					{/* Lease Option - Hidden by default, shown by preload if available */}
					<div
						className="position-relative payment-option-container py-3 d-flex border 
						justify-content-center middle-option border-top border-bottom transparent preload-hide-lease"
						style={{ flex: '1 1 0px' }}
						data-payment-type="lease"
					>
						<label
							htmlFor="payment_option_1_input"
							className="my-0 font-weight-normal w-100"
						>
							<div className="d-flex flex-row justify-content-center mt-1">
								<input
									type="radio"
									className="mt-0 mr-2"
									id="payment_option_1_input"
									value="Lease"
									name="paymentSelection"
									disabled
								/>
								<span className="ddc-font-size-small">
									Lease
								</span>
							</div>
							<div className="text-center">
								<span className="font-weight-bold ddc-font-size-base">
									<InlineDotsLoader />
								</span>
								<span className="text-muted ddc-font-size-xsmall">
									{' '}
									/ mo
								</span>
							</div>
						</label>
					</div>

					{/* Cash Option */}
					<div
						className="position-relative payment-option-container py-3 d-flex border 
						justify-content-center border-right rounded-right right-option 
						border-top border-bottom transparent"
						style={{ flex: '1 1 0px' }}
						data-payment-type="cash"
					>
						<label
							htmlFor="payment_option_2_input"
							className="my-0 font-weight-normal w-100"
						>
							<div className="d-flex flex-row justify-content-center mt-1">
								<input
									type="radio"
									className="mt-0 mr-2"
									id="payment_option_2_input"
									value="Cash"
									name="paymentSelection"
									disabled
								/>
								<span className="ddc-font-size-small">
									Cash
								</span>
							</div>
							<div className="text-center">
								<span className="font-weight-bold ddc-font-size-base">
									<InlineDotsLoader />
								</span>
							</div>
						</label>
					</div>

					{/* Inset borders for three-option layout - hidden by default */}
					<div
						className="inset-border"
						style={{ display: 'none' }}
						data-inset-border="1"
					/>
					<div
						className="inset-border"
						style={{ display: 'none' }}
						data-inset-border="2"
					/>
				</div>
			);
		}

		// If preload is disabled, show skeleton shimmer (traditional behavior)
		return (
			<div
				className="skeleton-shimmer border rounded"
				style={{ height: '70px', minHeight: '70px' }}
				data-testid="payment-skeleton-shimmer"
			>
				<div className="d-flex flex-row">
					<div className="ddc-font-size-small"> </div>
				</div>
			</div>
		);
	};

	const containerStyle = {
		justifyContent: 'space-evenly'
	};

	// Handle errors based on feature flag
	if (hasError && !paymentData) {
		if (enablePreload) {
			// Enhanced behavior: Show error message instead of infinite loader
			logger.warn(
				'WS-TRACK-MY-OFFER: PaymentSelector has error, showing error state',
				{
					hasError,
					isLoading,
					paymentData,
					selectedPayment,
					selectedDealType
				}
			);
			// Show error message similar to core module's error handling
			return (
				<div className="d-flex pt-4 rounded" style={containerStyle}>
					<div
						className="w-100 text-center p-4"
						style={{
							backgroundColor: '#f8f8f8',
							border: '1px solid #ddd',
							borderRadius: '4px'
						}}
					>
						<p
							style={{
								margin: 0,
								color: '#333',
								fontSize: '16px'
							}}
						>
							Payments for this vehicle are unavailable.
						</p>
						<p
							style={{
								marginTop: '10px',
								marginBottom: 0,
								color: '#666',
								fontSize: '14px'
							}}
						>
							Please call for more information.
						</p>
					</div>
				</div>
			);
		} else {
			// Old behavior: hide widget on error
			logger.error(
				'WS-TRACK-MY-OFFER: PaymentSelector hiding due to error',
				{
					hasError,
					isLoading,
					paymentData,
					selectedPayment,
					selectedDealType
				}
			);
			return null;
		}
	}

	const onPaymentSelect = (id) => {
		// set internal selected id
		setSelectedId(id);

		// Use dealType to select current deal type
		const dealType = paymentData[id]?.paymentLabel?.toLowerCase();
		logger.info(
			`WS-TRACK-MY-OFFER -> PaymentSelect -> Deal Type: ${dealType}`
		);

		eventBus?.emit('uds:setDealType:request', {
			dealType
		});

		sendTrackEvent({
			element: 'Build Offer Toggle',
			elementCTA: paymentData[id]?.paymentLabel,
			result: 'Change deal type'
		});
	};

	const getPaymentSelectOptions = () => {
		logger.info('WS-TRACK-MY-OFFER: getPaymentSelectOptions called', {
			paymentDataLength: paymentData?.length || 0,
			paymentData: paymentData?.map((d) => ({
				label: d.paymentLabel,
				payment: d.payment,
				key: d.key
			}))
		});

		// Display payment options when there is at least one valid payment
		// other invalid payment options are displayed as error states
		const hasValidPayment = paymentData.some((data) => data.payment > 0);

		// If no valid payment options, return empty
		if (!hasValidPayment) {
			logger.warn(
				'WS-TRACK-MY-OFFER: No valid payment options found after filtering',
				{
					originalData: paymentData,
					filtered: paymentData,
					originalCount: paymentData?.length || 0,
					filteredCount: 0
				}
			);
			return null;
		}

		return paymentData.map((data, index) => {
			const {
				payment,
				paymentTerm,
				paymentLabel,
				hasSpecial,
				key,
				hasTaxesAndFees,
				hasError: hasPaymentError,
				firstItem,
				lastItem
			} = data;

			return (
				<PaymentSelectOption
					id={index}
					paymentAmount={payment}
					term={paymentTerm}
					paymentTypeLabel={paymentLabel}
					isSelected={selectedId === index}
					onSelect={onPaymentSelect}
					firstItem={firstItem}
					lastItem={lastItem}
					hasSpecial={hasSpecial}
					isLoading={isLoading}
					key={key}
					hasTaxesAndFees={hasTaxesAndFees}
					hasError={hasPaymentError}
				/>
			);
		});
	};

	if (enablePreload && (isLoading || !paymentData)) {
		logger.info('WS-TRACK-MY-OFFER: Rendering preload placeholders', {
			enablePreload,
			isLoading,
			hasPaymentData: !!paymentData
		});
		// For preload mode, we need special handling to show/hide containers
		return <div className="pt-4 rounded">{getPlaceholders()}</div>;
	}

	const paymentOptions =
		isLoading || !paymentData
			? getPlaceholders()
			: getPaymentSelectOptions();

	logger.info('WS-TRACK-MY-OFFER: PaymentSelector final render', {
		isLoading,
		hasPaymentData: !!paymentData,
		paymentDataLength: paymentData?.length || 0,
		hasPaymentOptions: !!paymentOptions,
		enablePreload
	});

	return (
		!!paymentOptions && (
			<>
				<div className="d-flex pt-4 rounded" style={containerStyle}>
					{paymentOptions}
				</div>
				{showTaxesAndFeesDisclaimer && (
					<DisclaimerText
						text={labels.get('DISCLAIMER_TAXES_AND_FEES')}
						key="taxes-fees-disclaimer"
					/>
				)}
			</>
		)
	);
};

export default PaymentSelector;
